import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SentryUserContextSync } from "@/integrations/sentry-user-context";
import { SentryFeedbackButton } from "@/integrations/sentry-feedback-button";
import { ErrorFallback } from "@/integrations/sentry-error-fallback";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import Operator from "./pages/Operator";
import WorkFlow from "./pages/WorkFlow";
import InstructionList from "./pages/InstructionList";
import InstructionEditor from "./pages/InstructionEditor";
import InstructionPreview from "./pages/InstructionPreview";

import CarImport from "./pages/CarImport";
import CarsManagement from "./pages/CarsManagement";
import ImportSAP from "./pages/ImportSAP";
import UserManagement from "./pages/UserManagement";
import StationSelector from "./pages/StationSelector";
import ManageStations from "./pages/ManageStations";
import WorkOrderImport from "./pages/WorkOrderImport";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Test component to trigger error boundary
const TestError = () => {
  throw new Error("Test error boundary - this error was intentionally triggered");
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sentry.ErrorBoundary fallback={ErrorFallback}>
        <Toaster />
        <Sonner />
        <SentryFeedbackButton />
        <BrowserRouter>
          <AuthProvider>
            <SentryUserContextSync />
            <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operator"
              element={
                <ProtectedRoute>
                  <Operator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work/:carId"
              element={
                <ProtectedRoute>
                  <WorkFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/instructions"
              element={
                <ProtectedRoute>
                  <InstructionList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/instructions/new"
              element={
                <ProtectedRoute>
                  <InstructionEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/instructions/edit/:instructionId"
              element={
                <ProtectedRoute>
                  <InstructionEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/instructions/preview/:instructionId"
              element={
                <ProtectedRoute>
                  <InstructionPreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/car-import"
              element={
                <ProtectedRoute>
                  <CarImport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/work-orders"
              element={
                <ProtectedRoute>
                  <CarsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/import-sap"
              element={
                <ProtectedRoute>
                  <ImportSAP />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/user-management"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stations"
              element={
                <ProtectedRoute>
                  <StationSelector />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/stations"
              element={
                <ProtectedRoute>
                  <ManageStations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/work-order-import"
              element={
                <ProtectedRoute>
                  <WorkOrderImport />
                </ProtectedRoute>
              }
            />
            {/* Test route for error boundary (remove before production) */}
            <Route path="/test-error" element={<TestError />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </Sentry.ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
